import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { appMessage as message } from "../utils/appMessage";
import { useAuth } from "./AuthContext";
import { FALLBACK_VERSION, readStoredConsent } from "./cookieConsentStorage";
import { useConsentActions } from "./useConsentActions";
import { useServerConsentSync } from "./useServerConsentSync";

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const { user, isAuthenticated, appConfig, logout } = useAuth();
  const [consent, setConsent] = useState(() => readStoredConsent());
  const [bannerVisible, setBannerVisible] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState({
    first_party: true,
    third_party: true,
  });
  const lastSyncedRef = useRef("");
  const consentRef = useRef(consent);
  const reconciledAuthKeyRef = useRef("");
  const policyLogoutKeyRef = useRef("");

  const expectedVersion =
    appConfig?.cookieConsent?.consentVersion || FALLBACK_VERSION;
  const infoUrl = appConfig?.cookieConsent?.infoUrl || "/legal-notice";
  const currentUserId = user?.id || user?.user_id;

  useEffect(() => {
    consentRef.current = consent;
  }, [consent]);

  useEffect(() => {
    if (!isAuthenticated || !currentUserId) {
      lastSyncedRef.current = "";
      reconciledAuthKeyRef.current = "";
      policyLogoutKeyRef.current = "";
    }
  }, [currentUserId, isAuthenticated]);

  const isRejectAll = useCallback(
    (value) => !!value && !value.first_party && !value.third_party,
    [],
  );

  useEffect(() => {
    const stored = readStoredConsent();
    if (!stored || stored.consent_version !== expectedVersion) {
      setBannerVisible(true);
      return;
    }
    setConsent(stored);
    setBannerVisible(false);
  }, [expectedVersion]);

  useEffect(() => {
    if (!isAuthenticated || !currentUserId || !consent) {
      policyLogoutKeyRef.current = "";
      return;
    }

    const logoutKey = `${currentUserId}:${consent.consent_version}:${consent.first_party}:${consent.third_party}`;
    if (!isRejectAll(consent) || policyLogoutKeyRef.current === logoutKey)
      return;

    policyLogoutKeyRef.current = logoutKey;
    message.warning(
      "All cookie categories were rejected. Logging out to enforce policy.",
    );
    logout();
  }, [consent, currentUserId, isAuthenticated, isRejectAll, logout]);

  const {
    persistServerConsent,
    acceptAll,
    rejectAll,
    openSettings,
    closeSettings,
    saveConfigured,
  } = useConsentActions({
    expectedVersion,
    isAuthenticated,
    currentUserId,
    consent,
    consentRef,
    lastSyncedRef,
    setConsent,
    setBannerVisible,
    setSettingsDraft,
    setSettingsModalOpen,
  });

  useServerConsentSync({
    isAuthenticated,
    currentUserId,
    expectedVersion,
    consent,
    consentRef,
    lastSyncedRef,
    reconciledAuthKeyRef,
    setConsent,
    setBannerVisible,
    persistServerConsent,
  });

  const consentUpToDate =
    !!consent && consent.consent_version === expectedVersion;
  const canUseLocalLogin = !!consentUpToDate && consent.first_party;
  const canUseOpenIdLogin = !!consentUpToDate && consent.third_party;
  const isRejectedAll =
    !!consentUpToDate && !consent.first_party && !consent.third_party;

  const value = useMemo(
    () => ({
      consent,
      bannerVisible,
      settingsModalOpen,
      settingsDraft,
      setSettingsDraft,
      expectedVersion,
      infoUrl,
      consentUpToDate,
      canUseLocalLogin,
      canUseOpenIdLogin,
      isRejectedAll,
      acceptAll,
      rejectAll,
      openSettings,
      closeSettings,
      saveConfigured,
    }),
    [
      consent,
      bannerVisible,
      settingsModalOpen,
      settingsDraft,
      expectedVersion,
      infoUrl,
      consentUpToDate,
      canUseLocalLogin,
      canUseOpenIdLogin,
      isRejectedAll,
      acceptAll,
      rejectAll,
      openSettings,
      closeSettings,
      saveConfigured,
    ],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      "useCookieConsent must be used within CookieConsentProvider",
    );
  }
  return context;
}
